import { isNil } from 'lodash';
import { RRule, Options as RRuleOptions } from 'rrule';
import { AutoExtendConditionType } from '@shared/scenes/entity/components/fields/DateEditField/types';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import moment from '@shared/util/moment';
import { getTimezone } from '@shared/util/timezone';

export function getUTCDate(date: Date): Date {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hour = date.getHours();
  const minute = date.getMinutes();

  return new Date(Date.UTC(year, month, day, hour, minute));
}

export const MIN_COUNT = 2;
export const MAX_COUNT = 100;

export const DaysOfWeek = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];

export function getSharedOptions(date?: string, count?: number, until?: Date) {
  const dateObj = !isNil(date) ? new Date(date) : undefined;
  const dtstart = !isNil(dateObj) ? getUTCDate(dateObj) : undefined;
  const tzid = getTimezone();

  return { dtstart, count, tzid, until };
}

export function translateToMondayStart(n: number) {
  return n === 0 ? 6 : n - 1;
}

export function getDayOfWeek(date: Date) {
  return date.getUTCDay();
}

export function getNthWeekdayOfMonth(date: Date) {
  return Math.ceil(date.getUTCDate() / 7);
}

export function buildCustomRRule(
  rruleOptions: Partial<RRuleOptions> | undefined,
  newOptions: Partial<RRuleOptions>,
  clearExtraOptions: boolean = false,
): Partial<RRuleOptions> {
  // reset all but basics if freq changes or if explicit clearExtraOptions
  let mergedOptions: Partial<RRuleOptions>;
  if (clearExtraOptions || (!isNil(newOptions?.freq) && newOptions?.freq !== rruleOptions?.freq)) {
    const commonOptions = {
      dtstart: rruleOptions?.dtstart,
      tzid: rruleOptions?.tzid,
      count: rruleOptions?.count,
      until: rruleOptions?.until,
      interval: rruleOptions?.interval,
      freq: newOptions.freq ?? rruleOptions?.freq,
    };
    mergedOptions = { ...commonOptions, ...newOptions };
  } else {
    mergedOptions = { ...rruleOptions, ...newOptions };
  }

  // Validate YEARLY requirements: both bymonth and bymonthday must be present.
  if (mergedOptions?.freq === RRule.YEARLY) {
    const bymonth: number | number[] | undefined = (mergedOptions as any).bymonth;
    const bymonthday: number | number[] | undefined = (mergedOptions as any).bymonthday;

    const hasByMonth =
      !isNil(bymonth) &&
      (!Array.isArray(bymonth) || (Array.isArray(bymonth) && bymonth.length > 0));
    const hasByMonthDay =
      !isNil(bymonthday) &&
      (!Array.isArray(bymonthday) || (Array.isArray(bymonthday) && bymonthday.length > 0));

    if (!hasByMonth || !hasByMonthDay) {
      throw new Error('YEARLY recurrence requires both bymonth and bymonthday');
    }
  }

  return mergedOptions;
}

export const frequencyLabels = {
  [RRule.DAILY]: i18n('workflowTemplate.component.dateAndRepeat.frequency.daily'),
  [RRule.WEEKLY]: i18n('workflowTemplate.component.dateAndRepeat.frequency.weekly'),
  [RRule.MONTHLY]: i18n('workflowTemplate.component.dateAndRepeat.frequency.monthly'),
  [RRule.YEARLY]: i18n('workflowTemplate.component.dateAndRepeat.frequency.yearly'),
};

export const dayOfWeekLabels = {
  6: i18n('workflowTemplate.component.dateAndRepeat.dayOfWeek.sunday'),
  0: i18n('workflowTemplate.component.dateAndRepeat.dayOfWeek.monday'),
  1: i18n('workflowTemplate.component.dateAndRepeat.dayOfWeek.tuesday'),
  2: i18n('workflowTemplate.component.dateAndRepeat.dayOfWeek.wednesday'),
  3: i18n('workflowTemplate.component.dateAndRepeat.dayOfWeek.thursday'),
  4: i18n('workflowTemplate.component.dateAndRepeat.dayOfWeek.friday'),
  5: i18n('workflowTemplate.component.dateAndRepeat.dayOfWeek.saturday'),
};

// We're following iCalendar RFC here, not rrule, so we include
// the dtstart in the list, even if it doesn't fit in the
// selected schedule.
// If we have to force include dtstart, we also remove the
// last date in the schedule so the count is correct.
// https://github.com/jkbrzt/rrule?tab=readme-ov-file#differences-from-icalendar-rfc
export function getAllDates({
  rruleOptions,
  includeStart = false,
  offsetTz = false,
}: {
  rruleOptions: Partial<RRuleOptions> | undefined;
  includeStart?: boolean;
  offsetTz?: boolean;
}): Date[] {
  if (isNil(rruleOptions)) {
    return [];
  }

  // If neither count nor until is provided, limit the recurrence to MAX_COUNT occurrences
  const needsLimit = isNil(rruleOptions.count) && isNil(rruleOptions.until);
  const limitedOptions: Partial<RRuleOptions> = needsLimit
    ? { ...rruleOptions, count: MAX_COUNT }
    : rruleOptions;

  const ruleAll = new RRule(limitedOptions).all().map(date => getDateWithOffsetTz(date, offsetTz));

  if (
    includeStart &&
    !isNil(rruleOptions.dtstart) &&
    ruleAll[0].toISOString() !== getDateWithOffsetTz(rruleOptions.dtstart, offsetTz).toISOString()
  ) {
    return [rruleOptions.dtstart, ...ruleAll.slice(0, ruleAll.length - 1)];
  }

  return ruleAll;
}

export function getDateWithOffsetTz(date: Date, shouldOffset: boolean) {
  if (shouldOffset) {
    return new Date(date.toISOString().replace('Z', ''));
  }
  return date;
}

export function getRruleText(rruleOptions: Partial<RRuleOptions> | undefined) {
  const rrule = new RRule(rruleOptions);
  return rrule.toText();
}

/**
 * Given any date string within the target month (usually first of month),
 * returns the Date objects representing the start and end of the six-week
 * grid that our calendar component shows.  Logic mirrors generateSixWeekMonth
 * in `calendar/index.tsx` so that highlight calculations align with the
 * rendered calendar cells.
 */
export function getCalendarGridBounds(monthISO: string): { start: Date; end: Date } {
  const m = moment(monthISO);
  const startOfMonth = m.clone().startOf('month');
  const endOfMonth = m.clone().endOf('month');

  let startOfCalendar = startOfMonth.clone().startOf('week');

  // When February starts on the first, don't put 2 full weeks at end.
  if (startOfCalendar.date() === 1 && startOfMonth.month() === 1) {
    startOfCalendar = startOfCalendar.subtract(1, 'week');
  }

  let endOfCalendar = endOfMonth.clone().endOf('week');

  // Ensure at least 6 full weeks (42 days)
  while (endOfCalendar.diff(startOfCalendar, 'days') < 41) {
    endOfCalendar.add(1, 'week');
  }

  return { start: startOfCalendar.toDate(), end: endOfCalendar.toDate() };
}

/**
 * Return all rule occurrences between startDate and endDate (inclusive).
 *
 * If neither `count` nor `until` are present on the provided rruleOptions,
 * the recurrence is *NOT* capped here – callers should cap as needed.
 */
export function getDatesInRange({
  rruleOptions,
  startDate,
  endDate,
  includeStart = false,
  offsetTz = false,
}: {
  rruleOptions: Partial<RRuleOptions> | undefined;
  startDate: Date;
  endDate: Date;
  includeStart?: boolean;
  offsetTz?: boolean;
}): Date[] {
  if (isNil(rruleOptions)) {
    return [];
  }

  const rule = new RRule(rruleOptions);

  const betweenDates = rule
    .between(startDate, endDate, true)
    .map(date => getDateWithOffsetTz(date, offsetTz));

  if (
    includeStart &&
    !isNil(rruleOptions.dtstart) &&
    startDate <= (rruleOptions.dtstart as Date) &&
    (rruleOptions.dtstart as Date) <= endDate &&
    betweenDates.every(
      date =>
        date.toISOString() !==
        getDateWithOffsetTz(rruleOptions.dtstart as Date, offsetTz).toISOString(),
    )
  ) {
    return [rruleOptions.dtstart as Date, ...betweenDates];
  }

  return betweenDates;
}

/**
 * Return ISO strings for all rule occurrences that should be highlighted in a
 * calendar view for a given month grid.
 *
 * The highlighted range is bounded by:
 *   1. The six-week calendar grid for the requested month (see
 *      `getCalendarGridBounds`).
 *   2. `rruleOptions.until`, if present.
 *   3. An optional `endCapDate` provided by the caller (e.g. a user-selected
 *      end date).
 */
export function getHighlightDates({
  rruleOptions,
  monthStartISO,
  endCapDate,
  includeStart = true,
  offsetTz = true,
}: {
  rruleOptions?: Partial<RRuleOptions>;
  /** ISO string for any date within the month we want to render */
  monthStartISO: string;
  /** Optional hard stop for highlighting (takes precedence over rule.until) */
  endCapDate?: Date;
  includeStart?: boolean;
  offsetTz?: boolean;
}): string[] {
  if (isNil(rruleOptions)) {
    return [];
  }

  const { start, end } = getCalendarGridBounds(monthStartISO);

  // Determine the final date that should be considered for highlighting.
  let effectiveEnd = end;

  if (!isNil(rruleOptions.until) && (rruleOptions.until as Date) < effectiveEnd) {
    effectiveEnd = rruleOptions.until as Date;
  }

  if (!isNil(endCapDate) && endCapDate < effectiveEnd) {
    effectiveEnd = endCapDate;
  }

  return getDatesInRange({
    rruleOptions,
    startDate: start,
    endDate: effectiveEnd,
    includeStart,
    offsetTz,
  }).map(d => d.toISOString());
}

/**
 * Determine the initial auto-extend condition type based on provided values.
 */
export function getInitialAutoExtendConditionType({
  minimumInSeries,
  tagFilter,
  component,
}: {
  minimumInSeries?: number;
  tagFilter?: API.FilterByComponentFragment;
  component: API.DateComponent;
}): AutoExtendConditionType {
  const hasBothSetValues = !isNil(minimumInSeries) && !isNil(tagFilter);
  const hasBothDefaultValues =
    !isNil(component?.autoExtendDefault?.minimumInSeries) &&
    !isNil(component?.autoExtendDefault?.impliedFilterComponentId);

  if (!hasBothDefaultValues) {
    return 'custom';
  }

  const defaultAutoExtend = component?.autoExtendDefault;

  const isDefaultMatch =
    !isNil(defaultAutoExtend) &&
    hasBothSetValues &&
    minimumInSeries === defaultAutoExtend.minimumInSeries &&
    !tagFilter.value?.containsString?.negate &&
    tagFilter.componentId === component.autoExtendDefault?.impliedFilterComponentId;

  const forceDefault = hasBothDefaultValues && !hasBothSetValues && !isDefaultMatch;

  return isDefaultMatch || forceDefault ? 'default' : 'custom';
}

export function getInitialAutoExtendTagFilterOperator(tagFilter?: API.FilterByComponentFragment) {
  if (tagFilter?.value?.isEmpty?.value) {
    return 'empty';
  }
  if (tagFilter?.value?.containsString?.negate === true) {
    return 'does-not-contain';
  }
  return 'contains';
}

export function getOperatorLabel(selectedOperator: string) {
  if (selectedOperator === 'empty') {
    return i18n('card.actions.filters.fields.common.operator.empty');
  }
  if (selectedOperator === 'contains') {
    return i18n('card.actions.filters.fields.common.operator.autoContains');
  }
  return i18n('card.actions.filters.fields.common.operator.doesNotContainSelected');
}
